import React, { createContext, useContext, useEffect, useState } from 'react';
import { getPublicSettings } from '../api/siteSettings.api';

// Hardcoded fallbacks — used when the backend is unreachable so the site
// always renders. Admin-configured values override these at runtime.
const FALLBACK_BRANDING = {
  logoUrl: '/images/logo.png',
  logoDarkUrl: '/images/logo-dark.jpg',
  faviconUrl: '/favicon.ico',
  primaryColor: '#1E3A5F',
  accentColor: '#F97316',
  heroHeadline: '',
  heroSubtext: '',
};

const SiteSettingsContext = createContext({
  branding: FALLBACK_BRANDING,
  heroSlides: [],
  loading: true,
});

export const SiteSettingsProvider = ({ children }) => {
  const [branding, setBranding] = useState(FALLBACK_BRANDING);
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getPublicSettings()
      .then((data) => {
        if (!active) return;
        const settings = data?.settings || {};
        // Only override fallbacks with non-empty admin values.
        const merged = { ...FALLBACK_BRANDING };
        Object.keys(FALLBACK_BRANDING).forEach((key) => {
          if (settings[key]) merged[key] = settings[key];
        });
        setBranding({ ...merged, ...pick(settings, ['aboutHeadline', 'aboutText']) });
        setHeroSlides(Array.isArray(data?.heroSlides) ? data.heroSlides : []);
      })
      .catch(() => {
        /* keep fallbacks */
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  // Expose theme colors as CSS variables so var(--color-primary/accent) styles
  // can react to admin theming. (Static Tailwind tokens keep their defaults.)
  useEffect(() => {
    const root = document.documentElement;
    if (branding.primaryColor) root.style.setProperty('--color-primary', branding.primaryColor);
    if (branding.accentColor) root.style.setProperty('--color-accent', branding.accentColor);
  }, [branding.primaryColor, branding.accentColor]);

  // Apply favicon at runtime when the admin sets a custom one.
  useEffect(() => {
    if (!branding.faviconUrl || branding.faviconUrl === FALLBACK_BRANDING.faviconUrl) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }, [branding.faviconUrl]);

  return (
    <SiteSettingsContext.Provider value={{ branding, heroSlides, loading }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

const pick = (obj, keys) => keys.reduce((acc, k) => (obj[k] ? { ...acc, [k]: obj[k] } : acc), {});

export const useSiteSettings = () => useContext(SiteSettingsContext);

export default SiteSettingsContext;
